import { createSlice } from '@reduxjs/toolkit'
import { createApiAsyncThunk } from '@/services/apiService'
import { addFlashMessage } from '../flash/flashMessagesSlice'

const URL = 'process_tasks'

// ==================== ASYNC THUNKS ====================

export const fetchProcessTasks = createApiAsyncThunk(
  'processTasks/fetchAll',
  async (params = {}, { api, rejectWithValue }) => {
    try {
      const response = await api
        .url(URL)
        .query(params)
        .get()
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des tâches')
    }
  }
)

export const fetchProcessTask = createApiAsyncThunk(
  'processTasks/fetchOne',
  async (taskId, { api, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}`)
        .get()
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const updateProcessTask = createApiAsyncThunk(
  'processTasks/update',
  async ({ id, data }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${id}`)
        .put({ process_task: data })
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche mise à jour avec succès' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const completeProcessTask = createApiAsyncThunk(
  'processTasks/complete',
  async (taskId, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}/complete`)
        .post()
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche complétée avec succès' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const startProcessTask = createApiAsyncThunk(
  'processTasks/start',
  async (taskId, { api, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}/start`)
        .post()
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const cancelProcessTask = createApiAsyncThunk(
  'processTasks/cancel',
  async (taskId, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}/cancel`)
        .post()
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche annulée' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const assignProcessTask = createApiAsyncThunk(
  'processTasks/assign',
  async ({ taskId, userId }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${URL}/${taskId}/assign`)
        .post({ user_id: userId })
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche assignée avec succès' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteProcessTask = createApiAsyncThunk(
  'processTasks/delete',
  async (taskId, { api, dispatch, rejectWithValue }) => {
    try {
      await api
        .url(`${URL}/${taskId}`)
        .delete()
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche supprimée avec succès' }))
      return { id: taskId }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// ==================== PROCESS CONTAINER ACTIONS ====================

export const completeProcessContainer = createApiAsyncThunk(
  'processTasks/completeContainer',
  async (containerId, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`process_containers/${containerId}/complete`)
        .post()
      dispatch(addFlashMessage({ type: 'success', text: 'Process complété avec succès' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const archiveProcessContainer = createApiAsyncThunk(
  'processTasks/archiveContainer',
  async (containerId, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`process_containers/${containerId}/archive`)
        .post()
      dispatch(addFlashMessage({ type: 'success', text: 'Process archivé' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// ==================== INITIAL STATE ====================

const initialState = {
  items: [],
  current: null,
  loadingStatus: {
    fetchList: false,
    fetchItem: false,
    update: false,
    delete: false
  },
  requestStatus: {
    success: false,
    error: null
  },
  filters: {
    page: 1,
    limit: 20,
    status: '',
    bien_id: '',
    assigned_to_id: ''
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  }
}

// ==================== SLICE ====================

const processTasksSlice = createSlice({
  name: 'processTasks',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetFilters: (state) => {
      state.filters = initialState.filters
    },
    setCurrentTask: (state, action) => {
      state.current = action.payload
    },
    resetCurrentTask: (state) => {
      state.current = null
    },
    clearError: (state) => {
      state.requestStatus.error = null
    }
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder.addCase(fetchProcessTasks.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchProcessTasks.fulfilled, (state, action) => {
      state.loadingStatus.fetchList = false
      state.items = action.payload.tasks
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.requestStatus.success = true
    })
    builder.addCase(fetchProcessTasks.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // Fetch single task
    builder.addCase(fetchProcessTask.pending, (state) => {
      state.loadingStatus.fetchItem = true
    })
    builder.addCase(fetchProcessTask.fulfilled, (state, action) => {
      state.loadingStatus.fetchItem = false
      state.current = action.payload
    })
    builder.addCase(fetchProcessTask.rejected, (state, action) => {
      state.loadingStatus.fetchItem = false
      state.requestStatus.error = action.payload
    })

    // Update task
    builder.addCase(updateProcessTask.pending, (state) => {
      state.loadingStatus.update = true
    })
    builder.addCase(updateProcessTask.fulfilled, (state, action) => {
      state.loadingStatus.update = false
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      state.requestStatus.success = true
    })
    builder.addCase(updateProcessTask.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
    })

    // Complete task
    builder.addCase(completeProcessTask.fulfilled, (state, action) => {
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
    })

    // Start task
    builder.addCase(startProcessTask.fulfilled, (state, action) => {
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
    })

    // Cancel task
    builder.addCase(cancelProcessTask.fulfilled, (state, action) => {
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
    })

    // Assign task
    builder.addCase(assignProcessTask.fulfilled, (state, action) => {
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
      }
    })

    // Delete task
    builder.addCase(deleteProcessTask.pending, (state) => {
      state.loadingStatus.delete = true
    })
    builder.addCase(deleteProcessTask.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      state.items = state.items.filter(t => t.id !== action.payload.id)
      state.requestStatus.success = true
    })
    builder.addCase(deleteProcessTask.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })

    // Complete container - remove all tasks from this container
    builder.addCase(completeProcessContainer.fulfilled, (state, action) => {
      const containerId = action.payload.id
      state.items = state.items.filter(t => t.process_container?.id !== containerId)
    })

    // Archive container - remove all tasks from this container
    builder.addCase(archiveProcessContainer.fulfilled, (state, action) => {
      const containerId = action.payload.id
      state.items = state.items.filter(t => t.process_container?.id !== containerId)
    })
  }
})

export const {
  setFilters,
  resetFilters,
  setCurrentTask,
  resetCurrentTask,
  clearError
} = processTasksSlice.actions

export default processTasksSlice.reducer

